// react
import { Dispatch, SetStateAction, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
// node-modules
import axios, { AxiosResponse } from "axios";
// mui
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogContentText,
  useTheme,
  styled,
} from "@mui/material";
// src
import { config } from "../config/config";
import { Common } from "../../common/src";
import { LoadingButton } from "@mui/lab";
import { EDisplayType, ProjectDetailContext } from "../pages/ProjectDetail";
import { deleteContext } from "../pages/Home";

type Props = {
  projectID: string;
  projectName: string;
  openDialog: boolean;
  setOpenDialog: Dispatch<SetStateAction<boolean>>;
};

const DeleteProjectDialog = (props: Props) => {
  const { t } = useTranslation();
  const [whileLoading, setWhileLoading] = useState(false);
  const { getProjectList } = useContext(deleteContext);
  const { displayType } = useContext(ProjectDetailContext);

  const handleClose = () => {
    props.setOpenDialog(false);
  };

  const navigate = useNavigate();
  const location = useLocation();
  const searchCondition =
    location.state && (location.state as any).searchCondition
      ? (location.state as any).searchCondition
      : undefined;

  const removeProject = async () => {
    setWhileLoading(true);
    try {
      await axios
        .delete(`${config.serverHost}/api/v1/project/${props.projectID}`, {
          withCredentials: true,
        })
        .then((res: AxiosResponse<Common.ProjectDetail>) => {
          location.pathname === "/"
            ? getProjectList(new AbortController())
            : searchCondition
            ? navigate(`/`, { state: { searchCondition: searchCondition } })
            : navigate("/");
          if (displayType === EDisplayType.iOS) {
            (window as any).webkit.messageHandlers.callback.postMessage({
              type: "back",
              props: {},
            });
          }
        });
    } catch (err) {
      console.error(err);
    } finally {
      setWhileLoading(false);
      props.setOpenDialog(false);
    }
  };

  const theme = useTheme();

  const customColor = {
    color: theme.palette.base.contrast,
  };

  const customModalColor = {
    color: theme.palette.base.modal,
  };

  const customDialogBackgroundColor = {
    color: theme.palette.base.dialogBackground,
  };

  const customLoadingButtonColor = {
    background: theme.palette.btn.error.backgroundColor,
    border: theme.palette.btn.error.border,
    boxShadow: "none",
  };

  const CustomCanselButton = styled(Button)({
    background: theme.palette.base.modal,
    color: theme.palette.base.contrast,
    boxShadow: "none",
    "&:hover": {
      background: theme.palette.base.modal,
      color: theme.palette.base.contrast,
      boxShadow: "none",
    },
  });

  return (
    <Dialog
      open={props.openDialog}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{
        className: "del-dialog4",
        style: {
          background: customModalColor.color,
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: customDialogBackgroundColor.color,
          },
        },
      }}
    >
      <DialogTitle
        style={customColor}
        className="title-name"
        id="alert-dialog-title"
      >
        {t("REMOVE")}
        <Button
          style={customColor}
          className="x-btn btn-remove"
          onClick={handleClose}
        >
          ×
        </Button>
        <div className="border-comm" />
      </DialogTitle>
      <DialogContent>
        <DialogContentText style={customColor} className="del-dialog5">
          「{props.projectName}」{t("DELETE_CONFIRM")}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <CustomCanselButton
          size="small"
          variant="contained"
          onClick={handleClose}
          className="del-dialog6"
        >
          {t("CANCEL")}
        </CustomCanselButton>
        <LoadingButton
          style={customLoadingButtonColor}
          loading={whileLoading}
          size="small"
          variant="contained"
          onClick={removeProject}
          className="loading-white del-dialog7"
        >
          {t("REMOVE")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
export default DeleteProjectDialog;
